// Tela de login.
//
// Quando a conta está marcada com "exigir alteração de senha no próximo login"
// — o que vale para todo usuário recém-criado — o backend responde
// `mustChangePassword` e a própria tela abre os campos da senha nova. O usuário
// troca e entra na mesma ação, sem passar por e-mail.

import { useState } from 'react'
import { Alert, Button, Form, Input, Typography } from 'antd'
import { Link, useNavigate } from 'react-router-dom'
import { HOME_BY_ROLE, passwordSchema } from '@c1hd/shared'
import { ApiError } from '../lib/api'
import { useAuth } from '../lib/auth'
import styles from './Login.module.css'

interface FormValues {
  email: string
  password: string
  newPassword?: string
  confirmPassword?: string
}

export function LoginPage() {
  const { login } = useAuth()
  const navigate = useNavigate()
  const [form] = Form.useForm<FormValues>()
  const [mustChange, setMustChange] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [submitting, setSubmitting] = useState(false)

  async function onFinish(values: FormValues) {
    setError(null)
    setSubmitting(true)
    try {
      const result = await login({
        email: values.email,
        password: values.password,
        ...(mustChange && values.newPassword ? { newPassword: values.newPassword } : {}),
      })
      if (result.mustChangePassword || !result.user) {
        setMustChange(true)
        return
      }
      // Cada perfil tem a sua home: cliente vai para os chamados dele, técnico
      // para a fila, administrador para a visão geral.
      navigate(HOME_BY_ROLE[result.user.role], { replace: true })
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'Não foi possível entrar')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className={styles.page}>
      <div className={styles.card}>
        <div className={styles.brand}>
          <span className={styles.logoPlate}>
            <img src="/logo.png" alt="ContactOne" />
          </span>
          <span className={styles.brandText}>Central de Suporte</span>
        </div>

        <div>
          <h1 className={styles.title}>{mustChange ? 'Crie a sua senha' : 'Entrar'}</h1>
          <p className={styles.subtitle}>
            {mustChange
              ? 'Esta é a sua primeira entrada. Escolha uma senha para usar daqui em diante.'
              : 'Use o e-mail cadastrado na sua empresa.'}
          </p>
        </div>

        {mustChange && (
          <div className={styles.notice}>
            Sua senha atual é temporária e precisa ser trocada antes de continuar.
          </div>
        )}

        {error && <Alert type="error" message={error} showIcon />}

        <Form form={form} layout="vertical" onFinish={onFinish} requiredMark={false}>
          <Form.Item
            name="email"
            label="E-mail"
            rules={[
              { required: true, type: 'email', message: 'Informe um e-mail válido' },
            ]}
          >
            <Input autoComplete="username" placeholder="voce@suaempresa.com.br" />
          </Form.Item>

          <Form.Item
            name="password"
            label={mustChange ? 'Senha temporária' : 'Senha'}
            rules={[{ required: true, message: 'Informe a senha' }]}
          >
            <Input.Password autoComplete="current-password" />
          </Form.Item>

          {mustChange && (
            <>
              <Form.Item
                name="newPassword"
                label="Nova senha"
                rules={[
                  { required: true, message: 'Informe a nova senha' },
                  {
                    validator: (_, value: string) => {
                      const parsed = passwordSchema.safeParse(value ?? '')
                      return parsed.success
                        ? Promise.resolve()
                        : Promise.reject(new Error(parsed.error.issues[0]?.message))
                    },
                  },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>

              <Form.Item
                name="confirmPassword"
                label="Repita a nova senha"
                dependencies={['newPassword']}
                rules={[
                  { required: true, message: 'Repita a nova senha' },
                  {
                    validator: (_, value: string) =>
                      !value || value === form.getFieldValue('newPassword')
                        ? Promise.resolve()
                        : Promise.reject(new Error('As senhas não conferem')),
                  },
                ]}
              >
                <Input.Password autoComplete="new-password" />
              </Form.Item>
            </>
          )}

          <Form.Item style={{ marginBottom: 0 }}>
            <Button type="primary" htmlType="submit" block loading={submitting}>
              {mustChange ? 'Salvar e entrar' : 'Entrar'}
            </Button>
          </Form.Item>
        </Form>

        {!mustChange && (
          <div className={styles.footer}>
            <Link className={styles.link} to="/esqueci-minha-senha">
              Esqueci minha senha
            </Link>
            <Typography.Text type="secondary" style={{ fontSize: 13 }}>
              ContactOne
            </Typography.Text>
          </div>
        )}
      </div>
    </div>
  )
}
